<template>
	<div
		v-if="rewardGridItems && rewardGridItems.length > 0"
		class="rewards-grid"
		:class="`rewards-grid--${rewardGridItems.length > 1 ? 'multiple' : 'single'} rewards-grid--count-${count} rewards-grid--${context}`"
		:style="{
			gap: `${(dynamicSize?.gap ?? 16) / 16}rem`,
		}"
	>
		<div
			v-for="(upgrade, idx) in rewardGridItems"
			:key="idx"
			class="rewards-grid__item"
		>
			<Tooltip
				v-if="upgrade"
				:heading="translate(upgrade?.item?.heading as string)"
				:disabled="hasUpgradeAggregationDetails"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div class="rewards-grid__value">
						<div :class="`icon-upgrade-${upgrade.upgrade_id}-64`" />
					</div>
					<div class="rewards-grid__label">
						<span>upgrade</span>
					</div>
				</template>
				<template #tooltip>
					<span
						class="reward-tooltip"
						v-html="
							descriptionText(
								upgrade?.item?.description as string,
								upgrade?.item?.descriptionReplace as Record<
									string,
									string
								>,
							)
						"
					/>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import Tooltip from "@/components/Tooltip.vue";
import type { UpgradeItem } from "@/game/entities/config/upgrades";
import { upgrades } from "@/game/entities/config/upgrades";
import type { UpgradeSlotItem } from "@/types/shop";
import { OfferTemplate } from "@/types/shop";
import { Placements, RewardsGridContext } from "@/types/ui";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { canTranslate, translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	items: UpgradeSlotItem[] | null;
	context: RewardsGridContext;
	template: OfferTemplate;
}

const { items, context, template } = defineProps<Props>();

type RewardGridItem = UpgradeSlotItem & {
	item: UpgradeItem | null;
};

const rewardGridItems = computed((): RewardGridItem[] | null => {
	if (!items) return null;

	return (items as UpgradeSlotItem[]).map(
		(slotItem: UpgradeSlotItem): RewardGridItem => {
			const upgradeData = upgrades.get(
				slotItem.upgrade_id,
			) as UpgradeItem;

			return {
				...slotItem,
				item: upgradeData ?? null,
			};
		},
	);
});

const count = computed((): number => rewardGridItems.value?.length ?? 0);

const hasUpgradeAggregationDetails = computed((): boolean => {
	if (!rewardGridItems.value || rewardGridItems.value.length < 1)
		return false;
	if (
		context === RewardsGridContext.POPUP &&
		template === OfferTemplate.SLOTS_2 &&
		rewardGridItems.value.length === 1
	)
		return true;
	return false;
});

const dynamicSize = computed((): Record<string, number> => {
	// NOTE GD: Max 2 upgrades
	if (context === RewardsGridContext.POPUP) {
		switch (count.value) {
			case 1:
				return { gap: 0 };
			case 2:
				return { gap: 12 };
			default:
				return { gap: 12 };
		}
	} else {
		switch (count.value) {
			// TODO shop tile gfx
			default:
				return { icon: 80 };
		}
	}
});

const descriptionText = (
	description: string,
	descriptionReplace: Record<string, string>,
): string => {
	let text = translate(description);
	Object.entries(descriptionReplace).forEach(
		([key, value]: [string, string]): void => {
			const textTranslated = canTranslate(value)
				? translate(value)
				: value;
			text = replacePlaceholder(text, key, textTranslated);
		},
	);
	return text;
};
</script>

<style lang="scss" scoped>
.rewards-grid {
	@include flex-row-center;
	position: relative;
	z-index: 1;
	padding: 1.25rem 0.625rem 1.125rem;

	&--single,
	&--multiple {
		:deep(.upgrade-box) {
			@include unclickable-element;
			height: 100%;
		}
	}

	&--single {
		:deep(.upgrade-box-container .upgrade-box__content) {
			align-items: center;
		}
	}

	&--multiple {
		:deep(.upgrade-box-container) {
			.upgrade-box__heading .upgrade-box__heading__wrapper {
				justify-content: center;
			}

			.upgrade-box__content .description {
				display: none;
			}
		}
	}

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		@include flex-col-center;
		width: 100%;
		min-width: 4rem;
		height: 100%;

		.v-popper {
			width: 100%;
			height: 100%;
		}
	}

	&__value {
		@include flex-row-center;
		position: relative;
		width: 100%;
		height: 100%;

		&-item {
			&--horizontal {
				@include flex-row-center;
			}

			&--vertical {
				@include flex-col-center;
			}
		}
	}

	&__label {
		@include flex-row-center;
		position: relative;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		margin-top: -0.5rem;
		border-width: 0.063rem 0 0.063rem 0;
		border-style: solid;
		border-image-source: linear-gradient(
			to bottom,
			rgba(216, 169, 114, 1),
			rgba(226, 181, 145, 1)
		);
		border-image-slice: 1;
		width: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 1;
			mix-blend-mode: multiply;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			background: rgb(112, 62, 16);
			width: 100%;
			height: 100%;
			content: "";
		}

		span {
			position: relative;
			z-index: 1;
			color: #fff3dd;
			font-weight: 800;
			font-size: 0.625rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}
}
</style>
